import { contactWrap, FlowTemplate, leadIcon, sectionIcon, singleColumnPage, type RenderContext, type Zone } from "../base";
import { col, expanded, hrule, hspace, keep, linkText, padding, row, sized, text, vspace, type Node, type PageSpec } from "../ir";
import type { RenderEntry, RenderSection } from "../model";

const MARGIN = 46;
const LABEL = 100;
const GUTTER = 18;

/**
 * Swiss — International Typographic Style: section labels sit in a left
 * gutter, content in a strict right column, thin rules, lots of air.
 * (swiss_template.dart)
 */
export class SwissTemplate extends FlowTemplate {
  readonly info = {
    id: "swiss",
    name: "Swiss",
    tagline: "Minimal grid with labels in the margin",
    category: "minimal" as const,
    layout: "Grid",
    defaultAccent: 0xffdc2626,
    atsFriendly: true,
    supportsPhoto: false,
  };

  readonly fonts = { body: "inter" as const };

  zoneWidth(c: RenderContext): number {
    return c.format.width - MARGIN * 2 - LABEL - GUTTER;
  }

  heading(c: RenderContext, s: RenderSection): Node {
    return leadIcon(sectionIcon(c, s, c.t.accentText, c.t.fs(9.5)), this.gutterLabel(c, s.title), { gap: 5 });
  }

  private gutterLabel(c: RenderContext, value: string): Node {
    return text(value.toUpperCase(), c.t.style(c.t.heading.semiBold, 8.2, { color: c.t.accentText, letterSpacing: 1.3, lineSpacing: 1.5 }));
  }

  bulletGlyph(c: RenderContext): Node {
    return text("–", c.t.style(c.t.body.regular, 9.3, { color: c.t.muted, lineSpacing: 0 }));
  }

  entryHeader(c: RenderContext, e: RenderEntry): Node {
    const t = c.t;
    const title = e.title || e.subtitle;
    const subtitle = e.title ? e.subtitle : "";
    return col([
      row(
        [
          expanded(linkText(title, t.style(t.body.semiBold, 10, { color: t.ink, lineSpacing: 0 }), e.url)),
          e.dates && text(e.dates, t.style(t.body.regular, 8.4, { color: t.muted, lineSpacing: 0 })),
        ],
        { align: "start" }
      ),
      (subtitle || e.meta) &&
        padding(
          { top: 1.5 },
          text([subtitle, e.meta].filter(Boolean).join(", "), t.style(t.body.regular, 9, { color: t.muted, lineSpacing: 0 }))
        ),
    ]);
  }

  chips(c: RenderContext, items: string[], z: Zone): Node {
    return text(items.join(", "), this.paragraphStyle(c, z));
  }

  private gridRow(label: Node | null, content: Node): Node {
    return row([sized(LABEL, undefined, label), hspace(GUTTER), expanded(content)], { align: "start" });
  }

  /** Label in the gutter beside the first block; a hairline rule above. */
  sectionBlocks(c: RenderContext, s: RenderSection, z: Zone = "main"): Node[] {
    const content = this.contentBlocks(c, s, z);
    return [
      keep([hrule(0.6, c.t.ink), vspace(c.t.gap(8)), this.gridRow(this.heading(c, s), content[0] ?? vspace(0))]),
      ...content.slice(1).map((b) => this.gridRow(null, b)),
      vspace(c.t.gap(12)),
    ];
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const header = col([
      m.name && text(m.name, t.style(t.display.bold, 30, { color: t.ink, letterSpacing: -1, lineSpacing: 0 })),
      m.headline && vspace(4),
      m.headline && text(m.headline, t.style(t.body.regular, 12.5, { color: t.muted, lineSpacing: 0 })),
      vspace(t.gap(18)),
      m.contacts.length > 0 &&
        this.gridRow(
          this.gutterLabel(c, "Contact"),
          contactWrap(c, {
            style: t.style(t.body.regular, 8.6, { color: t.text, lineSpacing: 0 }),
            iconColor: t.muted,
            spacing: 12,
          })
        ),
      vspace(t.gap(16)),
    ]);
    return singleColumnPage(c, [MARGIN, MARGIN, MARGIN, 32], [keep([header]), ...this.allSections(c, m.sections)]);
  }
}
